import { useMutation } from '@apollo/client';
import type { InsertChildRiskOversightResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertChildRiskOversightResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertChildRiskOversightResultInput } from './useInsertChildRiskOversightResultTRPC';
import { useInsertChildRiskOversightResultTRPC } from './useInsertChildRiskOversightResultTRPC';

export const useInsertChildRiskOversightResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(
    InsertChildRiskOversightResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'risk_controlled_oversight_result');
        evictField(cache, 'risk_uncontrolled_oversight_result');
        evictField(cache, 'oversight_assessment');
        evictField(cache, 'risk_controlled_oversight_result_aggregate');
        evictField(cache, 'risk_uncontrolled_oversight_result_aggregate');
      },
    }
  );

  const trpcMutation = useInsertChildRiskOversightResultTRPC();

  const insertChildRiskOversightResult = async (
    variables: InsertChildRiskOversightResultInput
  ): Promise<InsertChildRiskOversightResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertChildRiskOversightResult(variables);
    }

    const result = await insertGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to insert risk oversight result');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertChildRiskOversightResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertChildRiskOversightResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
